import axios from "axios";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import ReactPaginate from "react-paginate";
import toast, { Toaster } from "react-hot-toast";
import DesignStudioModel from "./DesignStudio";
import {
  ProductImageStack,
  displayValue,
} from "../../../Components/OrderSpecsModal";
import Modal, { KeyValue } from "../../../Components/Modal";
import { formatMoney } from "../../../utils/money";

const REACT_APP_IMAGE_URL = process.env.REACT_APP_IMAGE_URL;

// Stepper Component
// Matches the stepper already fixed on productBuyerList.jsx /
// SupplierOfflineBuyerProductList.jsx (real workflow order: sales exec and
// graphic designer are assigned, the designer uploads a mockup, a supplier
// is assigned and sends a quote, the quote gets negotiated and admin sends
// a final quotation, the buyer approves it, tracking begins) — this page
// used to run its own, differently-ordered 7-step version instead of
// reusing it. Same bpl-stepper classes too, instead of one-off inline
// styles, so the same milestone looks the same everywhere it appears.
const Stepper = ({ order }) => {
  const steps = [
    { label: "Sales Executive", done: order?.sales_executive_id != null },
    { label: "Graphic Designer", done: order?.graphic_designer_id != null },
    { label: "Mockup", done: (order?.designs || []).length > 0 },
    { label: "Supplier", done: !!order?.supplier_assigned },
    {
      label: "Quotes",
      done: (order?.product || []).some(
        (p) => p?.quotation_price || p?.supplier_to_admin_quotation_price,
      ),
    },
    { label: "Final Quote", done: order?.final_price != null },
    {
      label: "Buyer Approval",
      done: (order?.product || []).some((p) => p?.status === "approved"),
    },
    {
      label: "Tracking",
      done: /track|complete|deliver/i.test(order?.order_status || ""),
    },
  ];

  return (
    <div className="bpl-stepper">
      {steps.map((s, i) => (
        <div className="bpl-step" key={i}>
          <div className="bpl-step__row">
            <div className={`bpl-step__dot ${s.done ? "is-done" : ""}`}>
              {s.done ? (
                <svg
                  width="13"
                  height="13"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="3"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              ) : (
                i + 1
              )}
            </div>
            {i < steps.length - 1 && (
              <div className={`bpl-step__line ${s.done ? "is-done" : ""}`} />
            )}
          </div>
          <span className={`bpl-step__lbl ${s.done ? "is-done" : ""}`}>
            {s.label}
          </span>
        </div>
      ))}
    </div>
  );
};

// An order can have several order_assigned rows — one per supplier who was
// ever invited to quote, most of them "rejected" once one supplier's quote
// is chosen. The approve/reject toggle below used to just grab product[0],
// whichever row happened to come back first from the DB (often a rejected
// one, or a supplier who was never even assigned — see order_assigned_id
// 248/249 for order #147, both null supplier_id). Clicking Approve then
// asked the backend about that row instead of the one actually pending
// approval, and buyerApproveReject correctly said "Quotation not found"
// for it. Pick the row that's actually pending, or already approved, over
// whatever happens to be first.
const getActiveQuotation = (product) => {
  const list = product || [];
  return (
    list.find((p) =>
      ["waiting_for_final_approval", "approved_by_sub_admin"].includes(
        p?.status,
      ),
    ) ||
    list.find((p) => p?.status === "approved") ||
    list[0]
  );
};

// Toggle Switch with Labels Component
// Toggle Switch Component with inline styles
const ToggleSwitch = ({
  isApproved,
  onToggle,
  loading = false,
  orderId,
  disabled, // ✅ new prop
}) => {
  const isDisabled = loading || disabled; // ✅ combine both flags

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <button
        type="button"
        style={{
          position: "relative",
          display: "inline-flex",
          height: "24px",
          width: "48px",
          flexShrink: 0,
          cursor: isDisabled ? "not-allowed" : "pointer",
          borderRadius: "9999px",
          border: "2px solid transparent",
          backgroundColor: isApproved ? "#10B981" : "#D1D5DB",
          transition: "background-color 0.2s ease-in-out",
          opacity: isDisabled ? 0.5 : 1,
          outline: "none",
        }}
        onClick={() => {
          if (!isDisabled) onToggle(); // ✅ prevent action if disabled
        }}
        disabled={isDisabled}
      >
        <span
          style={{
            pointerEvents: "none",
            display: "inline-block",
            height: "18px",
            width: "18px",
            transform: isApproved ? "translateX(16px)" : "translateX(0)",
            borderRadius: "9999px",
            backgroundColor: "white",
            boxShadow: "0 1px 2px 0 rgba(0, 0, 0, 0.05)",
            transition: "transform 0.2s ease-in-out",
          }}
        />
      </button>

      {loading && (
        <svg
          style={{
            animation: "spin 1s linear infinite",
            marginLeft: "8px",
            height: "16px",
            width: "16px",
            color: "#3B82F6",
          }}
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle
            style={{ opacity: 0.25 }}
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          ></circle>
          <path
            style={{ opacity: 0.75 }}
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          ></path>
        </svg>
      )}
    </div>
  );
};

// Approval Modal Component
const ApprovalModal = ({
  isOpen,
  onClose,
  onConfirm,
  loading,
  orderId,
  currentStatus,
}) => {
  const newStatus = !currentStatus;

  return (
    <Modal
      open={!!isOpen}
      onClose={loading ? undefined : onClose}
      title="Confirm Approval Action"
      subtitle={`Order #${orderId}`}
      size="sm"
      footer={
        <>
          <button
            type="button"
            className="al-btn"
            onClick={onClose}
            disabled={loading}
          >
            Cancel
          </button>
          <button
            type="button"
            className="al-btn"
            style={{
              background: "var(--color-danger-bg)",
              color: "var(--color-danger-text)",
              border: 0,
            }}
            onClick={() => onConfirm(false)}
            disabled={loading}
          >
            Reject
          </button>
          <button
            type="button"
            className="al-btn al-btn--primary"
            onClick={() => onConfirm(true)}
            disabled={loading}
          >
            Approve
          </button>
        </>
      }
    >
      <p style={{ margin: "0 0 14px", fontSize: 13.5 }}>
        You are about to{" "}
        <strong
          style={{
            color: newStatus
              ? "var(--color-success-text)"
              : "var(--color-danger-text)",
          }}
        >
          {newStatus ? "APPROVE" : "REJECT"}
        </strong>{" "}
        the quotation for order <span className="al-mono">#{orderId}</span>.
      </p>

      <div
        style={{
          display: "flex",
          gap: 9,
          padding: "11px 12px",
          borderRadius: 10,
          background: "var(--color-warning-bg)",
          color: "var(--color-warning-text)",
          fontSize: 12.5,
          fontWeight: 600,
        }}
      >
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          style={{ flexShrink: 0, marginTop: 1 }}
        >
          <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
          <line x1="12" y1="9" x2="12" y2="13" />
          <line x1="12" y1="17" x2="12.01" y2="17" />
        </svg>
        This action will update the order status and notify the relevant
        parties.
      </div>
    </Modal>
  );
};

const OutBound = () => {
  const Navigate = useNavigate();
  const location = useLocation();
  let is_alert = false;

  const [orderIds, setOrderIds] = useState(() => {
    return localStorage.getItem("order_id");
  });

  const handleRowClick = (id) => {
    const newId = orderIds === id ? null : id;
    setOrderIds(newId);

    if (newId) {
      localStorage.removeItem("order_id");
    } else {
      localStorage.removeItem("order_id");
    }
  };

  useEffect(() => {
    if (location.state !== null) {
      if (is_alert === false) {
        toast.success(location.state);
      }
      is_alert = true;
    }
    window.history.replaceState({}, location.state);
  }, []);

  const [data, setData] = useState([]);
  const [totalpage, setTotalPage] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [showDesignModal, setShowDesignModal] = useState(false);
  const [userId, setUserId] = useState(0);
  const [orderId, setOrderId] = useState(0);
  const [finalPrice, setFinalPrice] = useState("");
  const [finalDescription, setFinalDescription] = useState("");
  const [approvalDesc, setApprovalDesc] = useState("");
  const [showFinalApprovalModal, setShowFinalApprovalModal] = useState(false);
  const [selectedOrderId, setSelectedOrderId] = useState(null);
  const [monthFilter, setMonthFilter] = useState("");
  const [selectedImage, setSelectedImage] = useState(null);
  const [imageList, setImageList] = useState([]);
  const [showImagesModal, setShowImagesModal] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  // New states for approval functionality
  const [approvalLoading, setApprovalLoading] = useState({});
  const [approvalModal, setApprovalModal] = useState({
    isOpen: false,
    orderId: 0,
    currentStatus: false,
  });
  const [expandedRow, setExpandedRow] = useState(null);

  const user = JSON.parse(localStorage.getItem("user"));

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Open approval modal
  const handleOpenApprovalModal = (orderId, quotationId, currentStatus) => {
    setApprovalModal({
      isOpen: true,
      orderId,
      quotationId,
      currentStatus,
    });
  };

  // Close approval modal
  const handleCloseApprovalModal = () => {
    setApprovalModal({
      isOpen: false,
      orderId: 0,
      quotationId: 0,
      currentStatus: false,
    });
  };

  // Handle approval/rejection confirmation
  const handleApprovalConfirm = async (approved) => {
    const { orderId, quotationId } = approvalModal;

    setApprovalLoading((prev) => ({ ...prev, [orderId]: true }));

    try {
      const apiUrl = `${process.env.REACT_APP_URL}/admin/quotation/buyer/approve/${orderId}`;

      const payload = {
        quotation_id: quotationId,
        status: approved ? "approved" : "rejected",
      };

      const response = await axios.post(apiUrl, payload, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("Token")}`,
          "Content-Type": "application/json",
        },
      });

      if (response.data.success) {
        // This used to patch `item.status` locally, but the toggle's
        // approved/not-approved state actually reads
        // item.order.product[].status (a whole different field) — so the
        // patch never touched what the UI displays, and the toggle stayed
        // looking unchanged even though the approval had gone through.
        // Refetch instead of trying to hand-patch the nested shape.
        await fetchSupplierOrders();

        toast.success(
          `Quotation ${approved ? "approved" : "rejected"} successfully!`,
        );
        handleCloseApprovalModal();
      } else {
        throw new Error(
          response.data.message || "Failed to update approval status",
        );
      }
    } catch (error) {
      console.error("Approval error:", error);
      toast.error(
        error.response?.data?.message || "Failed to update approval status",
      );
    } finally {
      setApprovalLoading((prev) => ({ ...prev, [orderId]: false }));
    }
  };

  const handleOpenFinalQuotation = (orderId) => {
    setSelectedOrderId(orderId);
    setShowFinalApprovalModal(true);
  };

  const handleDesignOpenModal = (userId, orderId) => {
    setUserId(userId);
    setOrderId(orderId);
    setShowDesignModal(true);
  };

  const handleDesignCloseModal = () => {
    setShowDesignModal(false);
  };

  const handlePageClick = (e) => {
    setPage(e.selected + 1);
  };

  const handleWhatsAppClick = (phoneNumber) => {
    window.open(`https://wa.me/${phoneNumber}`, "_blank");
  };

  const handleCallClick = (phoneNumber) => {
    window.location.href = `tel:${phoneNumber}`;
  };

  const toggleRowExpand = (id) => {
    setExpandedRow((prev) => (prev === id ? null : id));
  };

  const fetchSupplierOrders = async () => {
    try {
      // page/search/monthFilter used to just be effect dependencies that
      // triggered a refetch of the exact same unfiltered URL — none of them
      // were actually appended to the request, so the search box, the
      // "Select Month" dropdown, and pagination all silently did nothing.
      const params = new URLSearchParams();
      params.append("page", page);
      params.append("limit", 20);
      if (search) params.append("search", search);
      if (monthFilter) params.append("month", monthFilter);

      const res = await axios.get(
        `${process.env.REACT_APP_URL}/admin/offline_supplier/orders/${user.user_id}?${params.toString()}`,
        {
          headers: { Authorization: `Bearer ${localStorage.getItem("Token")}` },
        },
      );

      // ✅ Correct API structure
      const responseData = res?.data?.data;
      const orders = responseData?.orders || [];

      // ✅ Flatten product data for display
      // ✅ Show only unique orders (no duplicate rows)
      const mergedData = orders.map((order) => ({
        order_id: order.order_id,
        order,
        user: order.user || {},
        mediaFiles: order.mediaFiles || [],
      }));

      setData(mergedData || []);

      setData(mergedData || []);
      setTotalPage(responseData?.pagination?.totalPages || 1);

      // ✅ Optional — populate quotation modal info from latest order
      const latestOrder = orders[0];
      if (latestOrder) {
        setFinalPrice(latestOrder.final_price || "");
        setFinalDescription(latestOrder.final_description || "");
        setApprovalDesc(latestOrder.approved_by || "");
      }
    } catch (err) {
      console.error("Error fetching supplier orders:", err);
      toast.error("Failed to load supplier orders");
      if (err?.response?.data?.message === "Unauthorized!") {
        Navigate("/");
      }
    }
  };

  useEffect(() => {
    fetchSupplierOrders();
  }, [page, search, selectedOrderId, monthFilter]);

  const renderDesktopView = () => (
    <table
      id="default-ordering"
      className="bpl-table bpl-table--wf"
      style={{ width: "100%" }}
      role="grid"
      aria-describedby="default-ordering_info"
    >
      <thead>
        <tr role="row">
          <th
            className="sorting col-2"
            tabIndex={0}
            rowSpan={1}
            colSpan={1}
            style={{ fontSize: "14px", color: "black", textAlign: "center" }}
          >
            Product Name
          </th>
          <th
            className="sorting col-2"
            tabIndex={0}
            rowSpan={1}
            colSpan={1}
            style={{ fontSize: "14px", color: "black", textAlign: "center" }}
          >
            Quantity
          </th>
          <th
            className="sorting col-2"
            tabIndex={0}
            rowSpan={1}
            colSpan={1}
            style={{ fontSize: "14px", color: "black", textAlign: "center" }}
          >
            Size
          </th>
          <th
            className="sorting col-2"
            tabIndex={0}
            rowSpan={1}
            colSpan={1}
            style={{ fontSize: "14px", color: "black", textAlign: "center" }}
          >
            Date &amp; Time
          </th>
          <th
            className="sorting col-2"
            tabIndex={0}
            rowSpan={1}
            colSpan={1}
            style={{ fontSize: "14px", color: "black", textAlign: "center" }}
          >
            Media File
          </th>
          <th
            className="sorting col-2"
            tabIndex={0}
            rowSpan={1}
            colSpan={1}
            style={{ fontSize: "14px", color: "black", textAlign: "center" }}
          >
            Design Studio
          </th>
          <th
            className="sorting col-2"
            tabIndex={0}
            rowSpan={1}
            colSpan={1}
            style={{ fontSize: "14px", color: "black", textAlign: "center" }}
          >
            Final Quotation
          </th>
          <th
            className="sorting col-2"
            tabIndex={0}
            rowSpan={1}
            colSpan={1}
            style={{ fontSize: "14px", color: "black", textAlign: "center" }}
          >
            Approve
          </th>
          <th
            className="sorting col-2"
            tabIndex={0}
            rowSpan={1}
            colSpan={1}
            style={{ fontSize: "14px", color: "black", textAlign: "center" }}
          >
            Track Order
          </th>
          <th
            className="sorting col-3"
            tabIndex={0}
            rowSpan={1}
            colSpan={1}
            style={{ fontSize: "14px", color: "black", textAlign: "center" }}
          >
            Action
          </th>
        </tr>
      </thead>
      <tbody>
        {data?.length !== 0 ? (
          data?.map((item, index) => (
            <>
              <tr
                role="row"
                key={index}
                onClick={() => handleRowClick(item?.id)}
                style={{
                  backgroundColor:
                    orderIds === String(item?.id) ? "#e3f2fd" : "transparent",
                }}
              >
                <td
                  style={{
                    fontSize: "14px",
                    color: "black",
                    textAlign: "center",
                  }}
                >
                  {item?.order?.title}
                </td>
                <td
                  style={{
                    fontSize: "14px",
                    color: "black",
                    textAlign: "center",
                  }}
                >
                  {item?.order?.quantity}
                </td>
                <td
                  style={{
                    fontSize: "14px",
                    color: "black",
                    textAlign: "center",
                  }}
                >
                  {/* size is stored JSON-stringified — this just rendered
                      the raw string (or nothing at all when null/empty).
                      Falls back to the catalog product's own size when the
                      order didn't specify a custom one. */}
                  {displayValue(
                    item?.order?.size || item?.order?.productInfo?.size,
                  )}
                </td>
                <td
                  style={{
                    fontSize: "14px",
                    color: "black",
                    textAlign: "center",
                  }}
                >
                  {new Date(item?.order?.created_at).toLocaleString("en-GB", {
                    day: "2-digit",
                    month: "2-digit",
                    year: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                    second: "2-digit",
                    hour12: false,
                  })}
                </td>
                <td
                  style={{
                    fontSize: "14px",
                    color: "black",
                    textAlign: "center",
                    verticalAlign: "middle",
                    padding: "12px 0",
                  }}
                  title="Hover to preview image"
                >
                  <div
                    style={{
                      fontSize: "14px",
                      color: "black",
                      textAlign: "center",
                      verticalAlign: "middle",
                      padding: "12px 0",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <ProductImageStack
                        images={(item?.mediaFiles || [])
                          .map((m) => m?.media_file)
                          .filter(Boolean)}
                        title="Click to preview"
                        circular
                        onOpen={() => {
                          setImageList(item?.mediaFiles);
                          setSelectedImage(item?.mediaFiles?.[0]);
                          setShowImagesModal(true);
                        }}
                      />
                    </div>
                  </div>
                </td>
                <td
                  style={{
                    fontSize: "14px",
                    color: "black",
                    textAlign: "center",
                    verticalAlign: "middle",
                    padding: "12px 0",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "12px",
                    }}
                  >
                    {/* The separate edit (pencil) icon opened the exact same
                        design modal as clicking the thumbnails already
                        does — redundant, removed. */}
                    <ProductImageStack
                      images={(item?.order?.designs || [])
                        .map((d) => d?.design_url)
                        .filter(Boolean)}
                      emptyLabel=""
                      title="Preview designs"
                      circular
                      onOpen={() =>
                        handleDesignOpenModal(
                          item?.order?.designs[0]?.designer_id,
                          item?.id,
                        )
                      }
                    />
                  </div>
                </td>
                <td style={{ textAlign: "center" }}>
                  {item?.order?.final_price ? (
                    <button
                      type="button"
                      className="wf-link"
                      title="View final quotation"
                      onClick={() =>
                        handleOpenFinalQuotation(item?.order?.order_id)
                      }
                    >
                      <span className="wf-price">
                        ₹{item?.order?.final_price}
                      </span>
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="var(--color-primary)"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <circle cx="12" cy="12" r="10" strokeWidth="2" />
                        <line
                          x1="12"
                          y1="8"
                          x2="12"
                          y2="8"
                          strokeWidth="2"
                          strokeLinecap="round"
                        />
                        <line
                          x1="12"
                          y1="12"
                          x2="12"
                          y2="16"
                          strokeWidth="2"
                          strokeLinecap="round"
                        />
                      </svg>
                    </button>
                  ) : (
                    <span className="wf-dash">—</span>
                  )}
                </td>
                <td style={{ fontSize: "14px", textAlign: "center" }}>
                  {(() => {
                    const activeQuotation = getActiveQuotation(
                      item.order?.product,
                    );
                    return (
                      <ToggleSwitch
                        isApproved={activeQuotation?.status === "approved"}
                        onToggle={() =>
                          handleOpenApprovalModal(
                            item?.order?.order_id,
                            activeQuotation?.order_assigned_id,
                            activeQuotation?.status === "approved",
                          )
                        }
                        loading={
                          approvalLoading[item?.order?.order_id] || false
                        }
                        orderId={item.order.order_id}
                        quotationId={activeQuotation?.order_assigned_id}
                        // Once approved, this is final — no reopening the
                        // modal to flip it back to reject.
                        disabled={
                          !item?.order?.final_price ||
                          activeQuotation?.status === "approved"
                        }
                      />
                    );
                  })()}
                </td>
                <td style={{ fontSize: "14px", textAlign: "center" }}>
                  <button
                    onClick={() => toggleRowExpand(item?.order?.order_id)}
                    style={{
                      color: "#0d6efd",
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                      padding: "6px", // 🔹 gives some clickable area
                      display: "inline-flex", // 🔹 ensures SVG is visible
                      alignItems: "center",
                      justifyContent: "center",
                      transition: "transform 0.3s ease",
                      transform:
                        expandedRow === item?.order?.order_id
                          ? "rotate(180deg)"
                          : "rotate(0deg)",
                    }}
                    title="Track Order"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      style={{
                        width: "22px", // ✅ visible size
                        height: "22px",
                        strokeWidth: 2,
                        stroke: "currentColor",
                      }}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </button>
                </td>

                <td style={{ fontSize: "14px" }}>
                  <div
                    style={{
                      display: "flex",
                      gap: "8px",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="#34B7F1"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      style={{ cursor: "pointer" }}
                      onClick={() => handleCallClick(item.user.phone)}
                    >
                      <path d="M22 16.92v3a2 2 0 0 1-2.18 2A19.79 19.79 0 0 1 3.1 5.18 2 2 0 0 1 5 3h3a2 2 0 0 1 2 1.72 12 12 0 0 0 .56 2.57 2 2 0 0 1-.45 2.11L9 11a16 16 0 0 0 5 5l1.6-1.1a2 2 0 0 1 2.1-.45 12 12 0 0 0 2.57.56A2 2 0 0 1 22 16.92z" />
                    </svg>
                    <svg
                      stroke="#25D366"
                      fill="#25D366"
                      strokeWidth="0"
                      viewBox="0 0 448 512"
                      className="text-green-500"
                      height="16"
                      width="16"
                      xmlns="http://www.w3.org/2000/svg"
                      style={{ cursor: "pointer" }}
                      onClick={() => handleWhatsAppClick(item.user.phone)}
                    >
                      <path d="M380.9 97.1C339 55.1 283.2 32 223.9 32c-122.4 0-222 99.6-222 222 0 39.1 10.2 77.3 29.6 111L0 480l117.7-30.9c32.4 17.7 68.9 27 106.1 27h.1c122.3 0 224.1-99.6 224.1-222 0-59.3-25.2-115-67.1-157zm-157 341.6c-33.2 0-65.7-8.9-94-25.7l-6.7-4-69.8 18.3L72 359.2l-4.4-7c-18.5-29.4-28.2-63.3-28.2-98.2 0-101.7 82.8-184.5 184.6-184.5 49.3 0 95.6 19.2 130.4 54.1 34.8 34.9 56.2 81.2 56.1 130.5 0 101.8-84.9 184.6-186.6 184.6zm101.2-138.2c-5.5-2.8-32.8-16.2-37.9-18-5.1-1.9-8.8-2.8-12.5 2.8-3.7 5.6-14.3 18-17.6 21.8-3.2 3.7-6.5 4.2-12 1.4-32.6-16.3-54-29.1-75.5-66-5.7-9.8 5.7-9.1 16.3-30.3 1.8-3.7.9-6.9-.5-9.7-1.4-2.8-12.5-30.1-17.1-41.2-4.5-10.8-9.1-9.3-12.5-9.5-3.2-.2-6.9-.2-10.6-.2-3.7 0-9.7 1.4-14.8 6.9-5.1 5.6-19.4 19-19.4 46.3 0 27.3 19.9 53.7 22.6 57.4 2.8 3.7 39.1 59.7 94.8 83.8 35.2 15.2 49 16.5 66.6 13.9 10.7-1.6 32.8-13.4 37.4-26.4 4.6-13 4.6-24.1 3.2-26.4-1.3-2.5-5-3.9-10.5-6.6z"></path>
                    </svg>
                  </div>
                </td>
              </tr>
              {expandedRow === item?.order?.order_id && (
                <tr>
                  <td colSpan={10} style={{ padding: 0 }}>
                    <div
                      style={{ backgroundColor: "#f8f9fa", padding: "20px" }}
                    >
                      <h4 style={{ marginBottom: "15px", fontWeight: "bold" }}>
                        Order Progress
                      </h4>
                      <Stepper order={item.order} />
                    </div>
                  </td>
                </tr>
              )}
            </>
          ))
        ) : (
          <tr>
            <td colSpan={10} style={{ padding: 0 }}>
              <div className="al-empty">
                <div className="al-empty__icon">
                  <svg
                    width="22"
                    height="22"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <circle cx="12" cy="12" r="10" />
                    <path d="M2 12h20M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
                  </svg>
                </div>
                <p className="al-empty__title">No Outbound Orders Found</p>
                <p className="al-empty__text">
                  Approved supplier orders will appear here.
                </p>
              </div>
            </td>
          </tr>
        )}
      </tbody>
    </table>
  );

  const renderMobileView = () => (
    <div className="row">
      {data?.length !== 0 ? (
        data?.map((item, index) => (
          <div className="col-12 mb-3" key={index}>
            <div
              className="card"
              style={{
                backgroundColor:
                  orderIds === String(item?.id) ? "#e3f2fd" : "white",
                border: "1px solid #ddd",
                borderRadius: "8px",
                boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                padding: "15px",
              }}
              onClick={() => handleRowClick(item?.id)}
            >
              <div className="d-flex justify-content-between mb-2">
                <div style={{ fontWeight: "bold", fontSize: "16px" }}>
                  Product Name:
                </div>
                <div>{item?.order?.title}</div>
              </div>

              <div className="d-flex justify-content-between mb-2">
                <div style={{ fontWeight: "bold", fontSize: "16px" }}>
                  Quantity:
                </div>
                <div>{item?.order?.quantity}</div>
              </div>

              <div className="d-flex justify-content-between mb-2">
                <div style={{ fontWeight: "bold", fontSize: "16px" }}>
                  Size:
                </div>
                <div>
                  {displayValue(
                    item?.order?.size || item?.order?.productInfo?.size,
                  )}
                </div>
              </div>

              <div className="d-flex justify-content-between mb-2">
                <div style={{ fontWeight: "bold", fontSize: "16px" }}>
                  Date & Time:
                </div>
                <div>
                  {new Date(item.created_at).toLocaleString("en-GB", {
                    day: "2-digit",
                    month: "2-digit",
                    year: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                    second: "2-digit",
                    hour12: false,
                  })}
                </div>
              </div>

              <div className="d-flex justify-content-between align-items-center mb-2">
                <div style={{ fontWeight: "bold", fontSize: "16px" }}>
                  Media Files:
                </div>
                <div style={{ display: "flex", alignItems: "center" }}>
                  {item?.mediaFiles?.slice(0, 3).map((media, index, arr) => (
                    <div
                      key={index}
                      style={{
                        position: "relative",
                        zIndex: arr.length - index,
                        marginLeft: index !== 0 ? "-15px" : "0",
                        cursor: "pointer",
                      }}
                      title="Click to preview"
                      onClick={(e) => {
                        e.stopPropagation();
                        setImageList(item?.mediaFiles);
                        setSelectedImage(media);
                        setShowImagesModal(true);
                      }}
                    >
                      <img
                        src={`${process.env.REACT_APP_IMAGE_URL}${media?.media_file}`}
                        alt={`Media ${index + 1}`}
                        style={{
                          width: "30px",
                          height: "30px",
                          borderRadius: "50%",
                          objectFit: "cover",
                          border: "2px solid white",
                          boxShadow: "0 0 2px rgba(0, 0, 0, 0.2)",
                        }}
                      />
                    </div>
                  ))}
                  {item.mediaFiles?.length > 3 && (
                    <div
                      style={{
                        marginLeft: "-15px",
                        width: "30px",
                        height: "30px",
                        borderRadius: "50%",
                        backgroundColor: "#d8d8d8",
                        color: "#000",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "12px",
                        fontWeight: "bold",
                        border: "2px solid white",
                        zIndex: 0,
                      }}
                    >
                      +{item.mediaFiles.length - 3}
                    </div>
                  )}
                </div>
              </div>

              <div className="d-flex justify-content-between align-items-center mb-2">
                <div style={{ fontWeight: "bold", fontSize: "16px" }}>
                  Design Studio:
                </div>
                <div style={{ display: "flex", alignItems: "center" }}>
                  {item?.order?.designs?.map((design, i, arr) => (
                    <div
                      key={i}
                      style={{ display: "flex", alignItems: "center" }}
                    >
                      <img
                        src={`${REACT_APP_IMAGE_URL}${design?.design_url}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDesignOpenModal(
                            item?.order?.designs[0]?.designer_id,
                            item?.id,
                          );
                        }}
                        style={{
                          width: "30px",
                          height: "30px",
                          borderRadius: "50%",
                          objectFit: "cover",
                          marginLeft: i !== 0 ? "-15px" : "0",
                          border: "2px solid white",
                          zIndex: arr.length - i,
                          cursor: "pointer",
                        }}
                        alt={`Design Image ${i + 1}`}
                      />
                    </div>
                  ))}
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDesignOpenModal(
                        item?.order?.designs[0]?.designer_id,
                        item?.order?.order_id,
                      );
                    }}
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#515365"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    style={{ cursor: "pointer", marginLeft: "5px" }}
                  >
                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                  </svg>
                </div>
              </div>

              <div className="d-flex justify-content-between align-items-center mb-2">
                <div style={{ fontWeight: "bold", fontSize: "16px" }}>
                  Final Quotation:
                </div>
                <div
                  onClick={(e) => {
                    e.stopPropagation();
                    handleOpenFinalQuotation(item?.order?.order_id);
                  }}
                  style={{
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: "5px",
                  }}
                >
                  <span
                    style={{
                      display: "inline-block",
                      minWidth: "50px",
                      minHeight: "28px",
                      lineHeight: "28px",
                      textAlign: "center",
                      padding: "0 8px",
                      border: "2px solid #0d6efd",
                      borderRadius: "6px",
                      fontSize: "12px",
                      fontWeight: 500,
                      color: "#212529",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {item?.order?.final_price}
                  </span>
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <circle
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="#17a2b8"
                      strokeWidth="2"
                    />
                    <line
                      x1="12"
                      y1="8"
                      x2="12"
                      y2="8"
                      stroke="#17a2b8"
                      strokeWidth="2"
                      strokeLinecap="round"
                    />
                    <line
                      x1="12"
                      y1="12"
                      x2="12"
                      y2="16"
                      stroke="#17a2b8"
                      strokeWidth="2"
                      strokeLinecap="round"
                    />
                  </svg>
                </div>
              </div>

              <div className="d-flex justify-content-between align-items-center mb-2">
                <div style={{ fontWeight: "bold", fontSize: "16px" }}>
                  Approve:
                </div>
                {(() => {
                  const activeQuotation = getActiveQuotation(
                    item.order?.product,
                  );
                  return (
                    <ToggleSwitch
                      isApproved={activeQuotation?.status === "approved"}
                      onToggle={() =>
                        handleOpenApprovalModal(
                          item?.order?.order_id,
                          activeQuotation?.order_assigned_id,
                          activeQuotation?.status === "approved",
                        )
                      }
                      loading={approvalLoading[item?.order?.order_id] || false}
                      orderId={item.order.order_id}
                      quotationId={activeQuotation?.order_assigned_id}
                      // Once approved, this is final — no reopening the
                      // modal to flip it back to reject.
                      disabled={
                        !item?.order?.final_price ||
                        activeQuotation?.status === "approved"
                      }
                    />
                  );
                })()}
              </div>

              <div className="d-flex justify-content-between align-items-center mb-2">
                <div style={{ fontWeight: "bold", fontSize: "16px" }}>
                  Track Order:
                </div>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleRowExpand(item?.order?.order_id);
                  }}
                  style={{
                    color: "#0d6efd",
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                  }}
                >
                  {expandedRow === item?.order?.order_id
                    ? "Hide Progress"
                    : "Show Progress"}
                </button>
              </div>

              {expandedRow === item?.order?.order_id && (
                <div
                  style={{
                    marginTop: "15px",
                    paddingTop: "15px",
                    borderTop: "1px solid #ddd",
                  }}
                >
                  <h5 style={{ fontWeight: "bold", marginBottom: "10px" }}>
                    Order Progress
                  </h5>
                  <Stepper order={item} />
                </div>
              )}

              <div className="d-flex justify-content-between align-items-center">
                <div style={{ fontWeight: "bold", fontSize: "16px" }}>
                  Actions:
                </div>
                <div style={{ display: "flex", gap: "10px" }}>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#34B7F1"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    style={{ cursor: "pointer" }}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleCallClick(item.user.phone);
                    }}
                  >
                    <path d="M22 16.92v3a2 2 0 0 1-2.18 2A19.79 19.79 0 0 1 3.1 5.18 2 2 0 0 1 5 3h3a2 2 0 0 1 2 1.72 12 12 0 0 0 .56 2.57 2 2 0 0 1-.45 2.11L9 11a16 16 0 0 0 5 5l1.6-1.1a2 2 0 0 1 2.1-.45 12 12 0 0 0 2.57.56A2 2 0 0 1 22 16.92z" />
                  </svg>
                  <svg
                    stroke="#25D366"
                    fill="#25D366"
                    strokeWidth="0"
                    viewBox="0 0 448 512"
                    height="20"
                    width="20"
                    xmlns="http://www.w3.org/2000/svg"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleWhatsAppClick(item.user.phone);
                    }}
                    style={{ cursor: "pointer" }}
                  >
                    <path d="M380.9 97.1C339 55.1 283.2 32 223.9 32c-122.4 0-222 99.6-222 222 0 39.1 10.2 77.3 29.6 111L0 480l117.7-30.9c32.4 17.7 68.9 27 106.1 27h.1c122.3 0 224.1-99.6 224.1-222 0-59.3-25.2-115-67.1-157zm-157 341.6c-33.2 0-65.7-8.9-94-25.7l-6.7-4-69.8 18.3L72 359.2l-4.4-7c-18.5-29.4-28.2-63.3-28.2-98.2 0-101.7 82.8-184.5 184.6-184.5 49.3 0 95.6 19.2 130.4 54.1 34.8 34.9 56.2 81.2 56.1 130.5 0 101.8-84.9 184.6-186.6 184.6zm101.2-138.2c-5.5-2.8-32.8-16.2-37.9-18-5.1-1.9-8.8-2.8-12.5 2.8-3.7 5.6-14.3 18-17.6 21.8-3.2 3.7-6.5 4.2-12 1.4-32.6-16.3-54-29.1-75.5-66-5.7-9.8 5.7-9.1 16.3-30.3 1.8-3.7.9-6.9-.5-9.7-1.4-2.8-12.5-30.1-17.1-41.2-4.5-10.8-9.1-9.3-12.5-9.5-3.2-.2-6.9-.2-10.6-.2-3.7 0-9.7 1.4-14.8 6.9-5.1 5.6-19.4 19-19.4 46.3 0 27.3 19.9 53.7 22.6 57.4 2.8 3.7 39.1 59.7 94.8 83.8 35.2 15.2 49 16.5 66.6 13.9 10.7-1.6 32.8-13.4 37.4-26.4 4.6-13 4.6-24.1 3.2-26.4-1.3-2.5-5-3.9-10.5-6.6z"></path>
                  </svg>
                </div>
              </div>
            </div>
          </div>
        ))
      ) : (
        <div className="al-empty">
          <div className="al-empty__icon">
            <svg
              width="22"
              height="22"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="12" cy="12" r="10" />
              <path d="M2 12h20M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
            </svg>
          </div>
          <p className="al-empty__title">No Outbound Orders Found</p>
          <p className="al-empty__text">
            Approved supplier orders will appear here.
          </p>
        </div>
      )}
    </div>
  );

  return (
    <div className="al">
      <Toaster position="top-center" reverseOrder={false} />
      <div className="al-head">
        <div>
          <h1 className="al-title">Supplier OutBound</h1>
          <p className="al-sub">Outbound supplier orders.</p>
        </div>
      </div>

      <div className="al-card bpl-card">
        <div className="widget-content widget-content-area p-0 box-shadow-none">
          <div className="table-responsive">
            <div
              id="default-ordering_wrapper"
              className="dataTables_wrapper container-fluid dt-bootstrap4"
            >
              <div className="al-toolbar">
                <div className="al-search">
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="var(--color-text-tertiary)"
                    strokeWidth="2"
                    strokeLinecap="round"
                  >
                    <circle cx="11" cy="11" r="7" />
                    <path d="m21 21-4.3-4.3" />
                  </svg>
                  <input
                    type="search"
                    placeholder="Search outbound orders…"
                    aria-controls="default-ordering"
                    onKeyDown={(e) => {
                      e.key === "Enter" &&
                        search !== "" &&
                        fetchSupplierOrders();
                    }}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                </div>
                <div className="al-select">
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <rect x="3" y="4" width="18" height="18" rx="2" />
                    <line x1="16" y1="2" x2="16" y2="6" />
                    <line x1="8" y1="2" x2="8" y2="6" />
                    <line x1="3" y1="10" x2="21" y2="10" />
                  </svg>
                  <select
                    value={monthFilter}
                    onChange={(e) => {
                      setMonthFilter(e.target.value);
                      fetchSupplierOrders();
                    }}
                  >
                    <option value="">Select Month</option>
                    <option value="All">All</option>
                    <option value="1">Last 1 Month</option>
                    <option value="3">Last 3 Months</option>
                    <option value="6">Last 6 Months</option>
                  </select>
                </div>
                <span
                  style={{
                    marginLeft: "auto",
                    fontSize: "11.5px",
                    color: "var(--color-text-tertiary)",
                    fontWeight: 600,
                  }}
                >
                  Tap a row to select its order
                </span>
              </div>
              <div className="row">
                <div className="col-sm-12">
                  {isMobile ? renderMobileView() : renderDesktopView()}

                  {/* Approval Modal */}
                  <ApprovalModal
                    isOpen={approvalModal.isOpen}
                    onClose={handleCloseApprovalModal}
                    onConfirm={handleApprovalConfirm}
                    loading={approvalLoading[approvalModal.orderId] || false}
                    orderId={approvalModal.orderId}
                    currentStatus={approvalModal.currentStatus}
                  />

                  {/* Existing modals */}
                  {showImagesModal && (
                    <div
                      style={{
                        position: "fixed",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "100%",
                        backgroundColor: "rgba(0, 0, 0, 0.6)",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        zIndex: 9999,
                      }}
                      onClick={() => setShowImagesModal(false)}
                    >
                      <div
                        style={{
                          backgroundColor: "white",
                          padding: "20px",
                          borderRadius: "10px",
                          maxWidth: "90%",
                          maxHeight: "90%",
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          overflowY: "auto",
                        }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        {selectedImage ? (
                          <img
                            src={`${process.env.REACT_APP_IMAGE_URL}${selectedImage.media_file}`}
                            alt="Full Size"
                            style={{
                              maxWidth: "100%",
                              maxHeight: "60vh",
                              borderRadius: "10px",
                              marginBottom: "16px",
                            }}
                          />
                        ) : (
                          <p>No image available</p>
                        )}

                        <div
                          style={{
                            display: "flex",
                            flexWrap: "wrap",
                            justifyContent: "center",
                            gap: "10px",
                            marginBottom: "16px",
                          }}
                        >
                          {imageList?.map((img, index) => (
                            <img
                              key={index}
                              src={`${process.env.REACT_APP_IMAGE_URL}${img?.media_file}`}
                              alt={`Thumbnail ${index + 1}`}
                              style={{
                                width: "80px",
                                height: "80px",
                                objectFit: "cover",
                                borderRadius: "6px",
                                cursor: "pointer",
                                border:
                                  selectedImage?.media_file === img?.media_file
                                    ? "2px solid #007bff"
                                    : "1px solid #ccc",
                              }}
                              onClick={() => setSelectedImage(img)}
                            />
                          ))}
                        </div>

                        <button
                          className="btn btn-danger"
                          onClick={() => setShowImagesModal(false)}
                        >
                          Close
                        </button>
                      </div>
                    </div>
                  )}

                  <DesignStudioModel
                    show={showDesignModal}
                    userId={userId}
                    orderId={orderId}
                    onClose={handleDesignCloseModal}
                  />

                  <Modal
                    open={showFinalApprovalModal}
                    onClose={() => setShowFinalApprovalModal(false)}
                    title="Final Quotation"
                    subtitle="Price already sent to the buyer"
                    size="sm"
                    footer={
                      <button
                        type="button"
                        className="al-btn"
                        onClick={() => setShowFinalApprovalModal(false)}
                      >
                        Close
                      </button>
                    }
                  >
                    <div className="al-kv">
                      <KeyValue label="Price">
                        {formatMoney(finalPrice)}
                      </KeyValue>
                      <KeyValue label="Status">
                        <span
                          className="al-pill"
                          style={
                            approvalDesc === "Rejected"
                              ? {
                                  background: "var(--color-danger-bg)",
                                  color: "var(--color-danger-text)",
                                }
                              : approvalDesc === "Approved"
                                ? {
                                    background: "var(--color-success-bg)",
                                    color: "var(--color-success-text)",
                                  }
                                : {
                                    background: "var(--color-warning-bg)",
                                    color: "var(--color-warning-text)",
                                  }
                          }
                        >
                          {approvalDesc || "Pending"}
                        </span>
                      </KeyValue>
                    </div>
                    <div style={{ marginTop: 16 }}>
                      <KeyValue label="Remark">
                        {finalDescription || "—"}
                      </KeyValue>
                    </div>
                  </Modal>
                </div>
              </div>
              <div className="al-foot">
                <span className="al-foot__info">
                  <strong>{data?.length || 0}</strong> order
                  {data?.length === 1 ? "" : "s"} shown
                </span>
                <div className="d-flex justify-content-center">
                  <div
                    className="dataTables_paginate paging_full_numbers"
                    id="alter_pagination_paginate"
                  >
                    {data?.length !== 0 ? (
                      <ReactPaginate
                        previousLabel={
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="#515365"
                            strokeWidth={2}
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            className="feather feather-arrow-left"
                          >
                            <line x1="19" y1="12" x2="5" y2="12"></line>
                            <polyline points="12 19 5 12 12 5"></polyline>
                          </svg>
                        }
                        nextLabel={
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="#515365"
                            strokeWidth={2}
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            className="feather feather-arrow-right"
                          >
                            <line x1="5" y1="12" x2="19" y2="12"></line>
                            <polyline points="12 5 19 12 12 19"></polyline>
                          </svg>
                        }
                        breakLabel={"..."}
                        pageCount={totalpage}
                        marginPagesDisplayed={2}
                        pageRangeDisplayed={2}
                        onPageChange={handlePageClick}
                        containerClassName={
                          "pagination justify-content-end m-0"
                        }
                        pageClassName={"page-item"}
                        pageLinkClassName={"page-link"}
                        previousClassName={"page-item"}
                        previousLinkClassName={"page-link"}
                        nextClassName={"page-item"}
                        nextLinkClassName={"page-link"}
                        breakClassName={"page-item"}
                        breakLinkClassName={"page-link"}
                        activeClassName={"active"}
                      />
                    ) : (
                      ""
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OutBound;
